/** Host-neutral match configuration and canonical dynamic match state. */
import { PlatformError } from '@djgg/core';
import {
  canonicalDigest,
  canonicalJson,
  cloneJson,
  validateJsonObject,
  type JsonObject,
} from '@djgg/gameplay-kit';

export const MATCH_CONFIG_VERSION = 1 as const;
export const MATCH_STATE_VERSION = 1 as const;
export const MATCH_PLAYER_LIMIT = 16;

export const MATCH_MODES = ['solo', 'vsBot', 'localVersus', 'onlineVersus'] as const;
export type MatchMode = (typeof MATCH_MODES)[number];

/** Why a bot is running. This host-facing vocabulary lives below sim-core. */
export const BOT_PURPOSES = [
  'demo',
  'opponent-casual',
  'opponent-adaptive',
  'evaluation',
] as const;
export type BotPurpose = (typeof BOT_PURPOSES)[number];

export interface MatchBotV1 {
  slot: number;
  tier: number;
  purpose: BotPurpose;
}

/** Automated runs reuse the declared game mode/rules without inventing human
 * seats. No presence, result, achievement or ranked writes are permitted.
 * Omission retains the existing interactive-session contract and digest. */
export interface AutomatedMatchSessionV1 {
  purpose: 'demo' | 'evaluation';
  control: 'cpu-only' | 'command-tape';
  persistence: 'none';
}

/** The complete deterministic match input shared byte-for-byte by every host. */
export interface MatchConfigV1 {
  schemaVersion: 1;
  creationId: string;
  version: number;
  artifactDigest: string;
  capabilityDigest: string;
  mode: MatchMode;
  seed: number;
  playerCount: number;
  teamBySlot: number[];
  chapter?: string;
  loadoutBySlot: Array<Readonly<Record<string, string>>>;
  rules: JsonObject;
  bots: MatchBotV1[];
  ranked: boolean;
  session?: AutomatedMatchSessionV1;
}

/** Version-owned audience policy. Spectators never become simulation seats or
 * alter the canonical match configuration/digest. */
export interface MatchSpectatorPolicyV1 {
  allowed: boolean;
  maxSpectators: number;
}

export interface MatchClockV1 {
  mode: 'none' | 'countdown' | 'countup';
  running: boolean;
  elapsedTicks: number;
  limitTicks?: number;
  remainingTicks?: number;
}

export interface MatchPeriodV1 {
  /** Zero-based period index. */
  index: number;
  count?: number;
  label?: string;
  overtime: boolean;
}

export interface MatchMetricV1 {
  id: string;
  values: number[];
}

export interface MatchObjectiveV1 {
  id: string;
  progressBySlot?: number[];
  target?: number;
  ownerSlot?: number | null;
  state: string;
}

/** Dynamic match state. Scores, players and outcome remain envelope fields. */
export interface MatchStateV1 {
  schemaVersion: 1;
  clock: MatchClockV1;
  period?: MatchPeriodV1;
  metrics: MatchMetricV1[];
  objective?: MatchObjectiveV1;
}

const DIGEST = /^[a-f0-9]{64}$/;
const ID = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/;
const LOADOUT_KEY = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

function fail(kind: string, message: string): never {
  throw new PlatformError('validation', `invalid ${kind}: ${message}`);
}

function record(value: unknown, path: string): Record<string, unknown> {
  try {
    return validateJsonObject(value, path) as Record<string, unknown>;
  } catch (error) {
    fail(path, error instanceof Error ? error.message : 'must be a JSON object');
  }
}

function exactKeys(
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[],
  path: string,
): void {
  const allowed = new Set([...required, ...optional]);
  for (const key of required) if (!(key in value)) fail(path, `missing ${key}`);
  for (const key of Object.keys(value)) if (!allowed.has(key)) fail(path, `unknown field ${key}`);
}

function safeInt(value: unknown, path: string, min = 0, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isSafeInteger(value) || (value as number) < min || (value as number) > max) {
    fail(path, `must be a safe integer in [${min}, ${max}]`);
  }
  return value as number;
}

function finite(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(path, 'must be a finite number');
  return value;
}

function boundedString(value: unknown, path: string, max: number, pattern?: RegExp): string {
  if (typeof value !== 'string' || value.length === 0 || value.length > max || value.trim() !== value) {
    fail(path, `must be a non-empty trimmed string of at most ${max} characters`);
  }
  if (pattern && !pattern.test(value)) fail(path, 'contains unsupported characters');
  return value;
}

function digest(value: unknown, path: string): string {
  if (typeof value !== 'string' || !DIGEST.test(value)) fail(path, 'must be 64 lowercase hexadecimal characters');
  return value;
}

function bool(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') fail(path, 'must be boolean');
  return value;
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child);
    Object.freeze(value);
  }
  return value;
}

function canonicalTeamIds(teams: readonly unknown[], playerCount: number): void {
  if (teams.length !== playerCount) fail('MatchConfigV1.teamBySlot', `must contain ${playerCount} entries`);
  const remap = new Map<number, number>();
  let next = 0;
  teams.forEach((raw, slot) => {
    const team = safeInt(raw, `MatchConfigV1.teamBySlot[${slot}]`, 0, playerCount - 1);
    if (!remap.has(team)) remap.set(team, next++);
    if (remap.get(team) !== team) {
      fail('MatchConfigV1.teamBySlot', 'team ids must be contiguous in first-seat order');
    }
  });
}

function validateLoadouts(value: unknown, playerCount: number): void {
  if (!Array.isArray(value) || value.length !== playerCount) {
    fail('MatchConfigV1.loadoutBySlot', `must contain ${playerCount} entries`);
  }
  value.forEach((item, slot) => {
    const loadout = record(item, `MatchConfigV1.loadoutBySlot[${slot}]`);
    const entries = Object.entries(loadout);
    if (entries.length > 32) fail(`MatchConfigV1.loadoutBySlot[${slot}]`, 'may contain at most 32 selections');
    for (const [key, choice] of entries) {
      boundedString(key, `MatchConfigV1.loadoutBySlot[${slot}] key`, 64, LOADOUT_KEY);
      boundedString(choice, `MatchConfigV1.loadoutBySlot[${slot}].${key}`, 128);
    }
  });
}

function validateBots(value: unknown, playerCount: number): void {
  if (!Array.isArray(value) || value.length > playerCount) fail('MatchConfigV1.bots', 'must be a bounded array');
  let previous = -1;
  value.forEach((item, index) => {
    const bot = record(item, `MatchConfigV1.bots[${index}]`);
    exactKeys(bot, ['slot', 'tier', 'purpose'], [], `MatchConfigV1.bots[${index}]`);
    const slot = safeInt(bot.slot, `MatchConfigV1.bots[${index}].slot`, 0, playerCount - 1);
    if (slot <= previous) fail('MatchConfigV1.bots', 'must be unique and ordered by slot');
    previous = slot;
    safeInt(bot.tier, `MatchConfigV1.bots[${index}].tier`, 0, 100);
    if (!BOT_PURPOSES.includes(bot.purpose as BotPurpose)) {
      fail(`MatchConfigV1.bots[${index}].purpose`, 'is unsupported');
    }
  });
}

/** Assert general frozen-config invariants; authoring policy is validated by creations. */
export function assertValidMatchConfigV1(value: unknown): asserts value is MatchConfigV1 {
  const config = record(value, 'MatchConfigV1');
  exactKeys(
    config,
    [
      'schemaVersion', 'creationId', 'version', 'artifactDigest', 'capabilityDigest',
      'mode', 'seed', 'playerCount', 'teamBySlot', 'loadoutBySlot', 'rules', 'bots', 'ranked',
    ],
    ['chapter', 'session'],
    'MatchConfigV1',
  );
  if (config.schemaVersion !== MATCH_CONFIG_VERSION) fail('MatchConfigV1.schemaVersion', 'must be 1');
  boundedString(config.creationId, 'MatchConfigV1.creationId', 128, ID);
  safeInt(config.version, 'MatchConfigV1.version', 1);
  digest(config.artifactDigest, 'MatchConfigV1.artifactDigest');
  digest(config.capabilityDigest, 'MatchConfigV1.capabilityDigest');
  if (!MATCH_MODES.includes(config.mode as MatchMode)) fail('MatchConfigV1.mode', 'is unsupported');
  safeInt(config.seed, 'MatchConfigV1.seed', 0, 0xffff_ffff);
  const playerCount = safeInt(config.playerCount, 'MatchConfigV1.playerCount', 1, MATCH_PLAYER_LIMIT);
  if (!Array.isArray(config.teamBySlot)) fail('MatchConfigV1.teamBySlot', 'must be an array');
  canonicalTeamIds(config.teamBySlot, playerCount);
  if (config.chapter !== undefined) boundedString(config.chapter, 'MatchConfigV1.chapter', 128, ID);
  validateLoadouts(config.loadoutBySlot, playerCount);
  const rules = record(config.rules, 'MatchConfigV1.rules');
  if (utf8ByteLength(canonicalJson(rules)) > 32 * 1024) fail('MatchConfigV1.rules', 'exceeds 32 KiB canonical JSON');
  validateBots(config.bots, playerCount);
  const botCount = (config.bots as unknown[]).length;
  const humanCount = playerCount - botCount;
  if (config.session !== undefined) {
    const session = record(config.session, 'MatchConfigV1.session');
    exactKeys(session, ['purpose', 'control', 'persistence'], [], 'MatchConfigV1.session');
    if (session.purpose !== 'demo' && session.purpose !== 'evaluation') fail('MatchConfigV1.session.purpose', 'must be demo or evaluation');
    if (session.control !== 'cpu-only' && session.control !== 'command-tape') fail('MatchConfigV1.session.control', 'must be cpu-only or command-tape');
    if (session.persistence !== 'none' || config.ranked !== false) fail('MatchConfigV1.session', 'automated sessions must be unranked and non-persisting');
    if (session.control === 'cpu-only' && botCount !== playerCount) fail('MatchConfigV1.session.control', 'cpu-only requires a bot in every seat');
    if (session.control === 'command-tape' && botCount !== 0) fail('MatchConfigV1.session.control', 'command-tape owns every seat and cannot also run live bots');
    for (const bot of config.bots as MatchBotV1[]) if (bot.purpose !== session.purpose) fail('MatchConfigV1.bots', 'bot purpose must match the automated session purpose');
    if (config.mode === 'solo' ? playerCount !== 1 : playerCount < 2) fail('MatchConfigV1.mode', 'automated seat count must preserve the declared game mode');
  }
  if (config.session === undefined && config.mode === 'solo' && (playerCount !== 1 || botCount !== 0)) {
    fail('MatchConfigV1.mode', 'solo requires one human seat and no bots');
  }
  if (config.session === undefined && config.mode === 'vsBot' && (botCount < 1 || humanCount < 1)) {
    fail('MatchConfigV1.mode', 'vsBot requires at least one human and one bot seat');
  }
  if (config.session === undefined && (config.mode === 'localVersus' || config.mode === 'onlineVersus') && humanCount < 2) {
    fail('MatchConfigV1.mode', `${String(config.mode)} requires at least two human seats`);
  }
  const ranked = bool(config.ranked, 'MatchConfigV1.ranked');
  if (ranked) {
    if (config.mode !== 'onlineVersus') fail('MatchConfigV1.ranked', 'requires onlineVersus');
    if (playerCount !== 2) fail('MatchConfigV1.ranked', 'requires exactly two seats');
    if ((config.bots as unknown[]).length !== 0) fail('MatchConfigV1.ranked', 'does not allow bots');
    if ((config.teamBySlot as unknown[])[0] !== 0 || (config.teamBySlot as unknown[])[1] !== 1) {
      fail('MatchConfigV1.ranked', 'requires two opposing canonical teams');
    }
  }
}

export function isValidMatchConfigV1(value: unknown): value is MatchConfigV1 {
  try {
    assertValidMatchConfigV1(value);
    return true;
  } catch {
    return false;
  }
}

/** Validate and deep-clone a config without changing its mode or filling policy defaults. */
export function normalizeMatchConfigV1(value: unknown): MatchConfigV1 {
  assertValidMatchConfigV1(value);
  return deepFreeze(cloneJson(value) as unknown as MatchConfigV1);
}

/** Copy the immutable runtime schema's audience policy at either host boundary. */
export function normalizeMatchSpectatorPolicyV1(value: unknown): MatchSpectatorPolicyV1 {
  const policy = record(value, 'MatchSpectatorPolicyV1');
  exactKeys(policy, ['allowed', 'maxSpectators'], [], 'MatchSpectatorPolicyV1');
  const allowed = bool(policy.allowed, 'MatchSpectatorPolicyV1.allowed');
  const maxSpectators = safeInt(policy.maxSpectators, 'MatchSpectatorPolicyV1.maxSpectators', 0, 100);
  if (allowed ? maxSpectators === 0 : maxSpectators !== 0) {
    fail('MatchSpectatorPolicyV1', 'allowed must agree with the spectator limit');
  }
  return deepFreeze({ allowed, maxSpectators });
}

export function canonicalMatchConfigV1(value: unknown): string {
  return canonicalJson(normalizeMatchConfigV1(value));
}

export async function matchConfigDigestV1(value: unknown): Promise<string> {
  return canonicalDigest(normalizeMatchConfigV1(value));
}

/**
 * Digest a creations-schema-selected shared lobby projection. Protocol treats
 * its fields as strict JSON; creations decides which choices are shared.
 */
export async function sharedMatchSelectionDigestV1(value: unknown): Promise<string> {
  return canonicalDigest(validateJsonObject(value, 'SharedMatchSelectionV1'));
}

/** Validator form used by authoring/runtime boundaries that need a returned value. */
export function validateMatchConfigV1(value: unknown, path = 'MatchConfigV1'): MatchConfigV1 {
  try {
    return normalizeMatchConfigV1(value);
  } catch (error) {
    if (path === 'MatchConfigV1') throw error;
    throw new PlatformError(
      'validation',
      `${path}: ${error instanceof Error ? error.message : 'invalid MatchConfigV1'}`,
    );
  }
}

function validateClock(value: unknown): void {
  const clock = record(value, 'MatchStateV1.clock');
  exactKeys(clock, ['mode', 'running', 'elapsedTicks'], ['limitTicks', 'remainingTicks'], 'MatchStateV1.clock');
  if (!['none', 'countdown', 'countup'].includes(clock.mode as string)) fail('MatchStateV1.clock.mode', 'is unsupported');
  bool(clock.running, 'MatchStateV1.clock.running');
  const elapsed = safeInt(clock.elapsedTicks, 'MatchStateV1.clock.elapsedTicks');
  const limit = clock.limitTicks === undefined
    ? undefined
    : safeInt(clock.limitTicks, 'MatchStateV1.clock.limitTicks', 1);
  const remaining = clock.remainingTicks === undefined
    ? undefined
    : safeInt(clock.remainingTicks, 'MatchStateV1.clock.remainingTicks');
  if (clock.mode === 'none') {
    if (clock.running !== false || limit !== undefined || remaining !== undefined) {
      fail('MatchStateV1.clock', 'none mode must be stopped and omit limit/remaining ticks');
    }
  } else if (clock.mode === 'countdown') {
    if (limit === undefined || remaining === undefined) fail('MatchStateV1.clock', 'countdown requires limitTicks and remainingTicks');
    if (remaining !== Math.max(0, limit - elapsed)) fail('MatchStateV1.clock.remainingTicks', 'does not match limitTicks - elapsedTicks');
  } else if (remaining !== undefined) {
    fail('MatchStateV1.clock.remainingTicks', 'is only valid for countdown clocks');
  }
}

/** Validate canonical state using `playerCount` when the envelope supplies it. */
export function assertValidMatchStateV1(value: unknown, playerCount?: number): asserts value is MatchStateV1 {
  const state = record(value, 'MatchStateV1');
  exactKeys(state, ['schemaVersion', 'clock', 'metrics'], ['period', 'objective'], 'MatchStateV1');
  if (state.schemaVersion !== MATCH_STATE_VERSION) fail('MatchStateV1.schemaVersion', 'must be 1');
  if (playerCount !== undefined) safeInt(playerCount, 'playerCount', 1, MATCH_PLAYER_LIMIT);
  validateClock(state.clock);

  if (state.period !== undefined) {
    const period = record(state.period, 'MatchStateV1.period');
    exactKeys(period, ['index', 'overtime'], ['count', 'label'], 'MatchStateV1.period');
    const index = safeInt(period.index, 'MatchStateV1.period.index');
    const count = period.count === undefined ? undefined : safeInt(period.count, 'MatchStateV1.period.count', 1);
    if (count !== undefined && index >= count) fail('MatchStateV1.period.index', 'must be below count');
    if (period.label !== undefined) boundedString(period.label, 'MatchStateV1.period.label', 64);
    bool(period.overtime, 'MatchStateV1.period.overtime');
  }

  if (!Array.isArray(state.metrics) || state.metrics.length > 32) fail('MatchStateV1.metrics', 'must contain at most 32 metrics');
  const metricIds = new Set<string>();
  state.metrics.forEach((item, index) => {
    const metric = record(item, `MatchStateV1.metrics[${index}]`);
    exactKeys(metric, ['id', 'values'], [], `MatchStateV1.metrics[${index}]`);
    const id = boundedString(metric.id, `MatchStateV1.metrics[${index}].id`, 64, ID);
    if (metricIds.has(id)) fail('MatchStateV1.metrics', `duplicate id ${id}`);
    metricIds.add(id);
    if (!Array.isArray(metric.values) || metric.values.length > MATCH_PLAYER_LIMIT) {
      fail(`MatchStateV1.metrics[${index}].values`, 'must be a bounded array');
    }
    if (playerCount !== undefined && metric.values.length !== playerCount) {
      fail(`MatchStateV1.metrics[${index}].values`, `must contain ${playerCount} entries`);
    }
    metric.values.forEach((entry, slot) => finite(entry, `MatchStateV1.metrics[${index}].values[${slot}]`));
  });

  if (state.objective !== undefined) {
    const objective = record(state.objective, 'MatchStateV1.objective');
    exactKeys(objective, ['id', 'state'], ['progressBySlot', 'target', 'ownerSlot'], 'MatchStateV1.objective');
    boundedString(objective.id, 'MatchStateV1.objective.id', 64, ID);
    boundedString(objective.state, 'MatchStateV1.objective.state', 64, ID);
    if (objective.progressBySlot !== undefined) {
      if (!Array.isArray(objective.progressBySlot) || objective.progressBySlot.length > MATCH_PLAYER_LIMIT) {
        fail('MatchStateV1.objective.progressBySlot', 'must be a bounded array');
      }
      if (playerCount !== undefined && objective.progressBySlot.length !== playerCount) {
        fail('MatchStateV1.objective.progressBySlot', `must contain ${playerCount} entries`);
      }
      objective.progressBySlot.forEach((entry, slot) => finite(entry, `MatchStateV1.objective.progressBySlot[${slot}]`));
    }
    if (objective.target !== undefined) finite(objective.target, 'MatchStateV1.objective.target');
    if (objective.ownerSlot !== undefined && objective.ownerSlot !== null) {
      safeInt(objective.ownerSlot, 'MatchStateV1.objective.ownerSlot', 0, (playerCount ?? MATCH_PLAYER_LIMIT) - 1);
    }
  }
}

export function isValidMatchStateV1(value: unknown, playerCount?: number): value is MatchStateV1 {
  try {
    assertValidMatchStateV1(value, playerCount);
    return true;
  } catch {
    return false;
  }
}
